import clsx from 'clsx';
import { CloudOff, Laptop, Server } from 'lucide-react';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const SEGMENTS = [
  { label: 'Mail', detail: '12,408 threads · 1.1 GB', share: 55, className: 'bg-blue-9' },
  { label: 'Attachments', detail: '3,210 files · 820 MB', share: 41, className: 'bg-blue-7' },
  { label: 'Search index', detail: 'Every message · 86 MB', share: 4, className: 'bg-blue-5' },
] as const;

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

/** Storage bars for the device and for our servers, like a system storage view. */
const StorageGraphic: React.FC = () => (
  <div className="flex flex-col gap-8" aria-hidden>
    <div>
      <Header icon={<Laptop />} label="Your device" total="2.0 GB" />
      <div className="bg-gray-3 flex h-7 gap-0.5 overflow-hidden rounded-md">
        {SEGMENTS.map(({ label, share, className }, index) => (
          <i
            key={label}
            className={clsx('landing-storage-segment block h-full', className)}
            style={{ flex: share, animationDelay: `${index * 0.15}s` }}
          />
        ))}
      </div>
      <div className="mt-3.5 grid grid-cols-1 gap-2.5 md:grid-cols-3 md:gap-4">
        {SEGMENTS.map(({ label, detail, className }) => (
          <div key={label} className="text-gray-11 flex flex-col gap-0.5 text-xs">
            <b className="text-gray-12 flex items-center gap-1.5 text-[13px] leading-5 font-medium">
              <s className={clsx('size-2 shrink-0 rounded-[2px]', className)} />
              {label}
            </b>
            {detail}
          </div>
        ))}
      </div>
    </div>
    <div>
      <Header icon={<Server />} label="Our servers" total="0 bytes" />
      <div className="border-gray-7 h-7 rounded-md border border-dashed" />
      <p className="text-gray-11 mt-2.5 text-xs">No mail, attachments or search data.</p>
    </div>
    <p className="border-gray-5 text-gray-11 flex items-center gap-2 border-t pt-6 text-[13px] leading-5">
      <CloudOff className="size-3.5 shrink-0" />
      Because it’s all local, search, archive and drafts work offline.
    </p>
  </div>
);

const Header: React.FC<{ icon: React.ReactNode; label: string; total: string }> = ({
  icon,
  label,
  total,
}) => (
  <div className="mb-2.5 flex items-center justify-between text-[13px] leading-5 font-medium">
    <span className="[&_svg]:text-gray-11 flex items-center gap-1.5 [&_svg]:size-3.5">
      {icon}
      {label}
    </span>
    <span className="tabular-nums">{total}</span>
  </div>
);

export default StorageGraphic;
